import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useLocaleContext } from '../../../../context/LocaleContext';
import cloneDeep from 'lodash.clonedeep';
import { removeDuplicates } from '../../../../helpers/remove-duplicates';
import FiltersTags from '../../../Filters/FiltersTags';
import FiltersSearch from '../../../Filters/FiltersSearch';
import FiltersOrganization from '../../../Filters/FiltersOrganization';
import { CButton, CTags } from '../../../UI';

import './style.scss';

function MembersPageFilters({
    event,
    initialMembers = null,
    onFilter,
    hasSearch = true,
    hasTagsFilter = true,
    hasSignsList = true,
    hasOrganizationFilter = true,
}) {
    const $locale = useLocaleContext();
    const [members, setMembers] = useState([]);

    const [filtersState, setFiltersState] = useReducer(
        (prev, next) => {
            return { ...prev, ...next };
        },
        {
            selectedSigns: [],
            selectedTags: [],
            searchQuery: '',
            suggestionOrganization: '',
        }
    );

    useEffect(() => {
        setMembers(initialMembers ?? event.members);
    }, [initialMembers]);

    const tagsList = useMemo(() => {
        return removeDuplicates(members
            .map(({tags}) => tags)
            .flat(),
            'id'
        );
    }, [members]);

    const signsList = useMemo(() => {
        return removeDuplicates(members
            .map(({roles}) => roles)
            .flat(),
            'id'
        );
    }, [members]);

    const organizationsList = useMemo(() => {
        const list = members
            .map((member) => $locale.getProperty(member, 'company'))
            .filter(Boolean)
        ;

        return [...new Set(list)];
    }, [members]);

    const handleUpdateStateArray = useCallback((key, value) => {
        if (!value) {
            setFiltersState({[key]: []});

            return;
        }

        if (!filtersState[key].includes(value)) {
            setFiltersState({[key]: [... new Set([...filtersState[key], value])]});

            return;
        }

        setFiltersState({[key]: filtersState[key].filter((item) => item !== value)});
    });

    useEffect(() => {
        let filteredMembers = cloneDeep(initialMembers ?? event.members);

        if (filtersState.selectedSigns?.length) {
            filteredMembers = filteredMembers.filter(({roles}) => roles.some((role) => filtersState.selectedSigns.includes(role.id)));
        }

        if (filtersState.selectedTags?.length) {
            filteredMembers = filteredMembers.filter(({tags}) => tags.some((tag) => filtersState.selectedTags.includes(tag.id)));
        }

        if (filtersState.searchQuery?.length) {
            filteredMembers = filteredMembers.filter((member) => {
                return Object.values(member).some((value) => {
                    return typeof value === 'string'
                        && !!value.length
                        && value.toLowerCase().includes(filtersState.searchQuery.toLowerCase());
                });
            });
        }

        if (filtersState.suggestionOrganization?.length) {
            filteredMembers = filteredMembers.filter((member) => {
                return $locale.getProperty(member, 'company').toLowerCase() === filtersState.suggestionOrganization.toLowerCase();
            });
        }

        onFilter(filteredMembers);
    }, [filtersState]);

    return (
        <>
            <div className="members-filters">
                <div className="members-filters-signs">
                    {
                        (hasSignsList && signsList?.length > 1) &&
                        signsList?.map((sign) => (
                            <CButton
                                key={sign.id}
                                label={$locale.getProperty(sign, 'title')}
                                size="xs"
                                theme={filtersState.selectedSigns.includes(sign.id) ? 'highlight' : 'default'}
                                icon={filtersState.selectedSigns.includes(sign.id) ? 'close' : ''}
                                iconPosition="right"
                                className="members-filters-signs__button"
                                onClick={() => handleUpdateStateArray('selectedSigns', sign.id)}
                            />
                        ))
                    }
                    {
                        !!filtersState.selectedSigns.length &&
                        <CButton
                            label={$locale.get('label.reset.all')}
                            size="xs"
                            theme="highlight"
                            icon="close"
                            iconPosition="right"
                            className="members-filters-signs__button"
                            onClick={() => handleUpdateStateArray('selectedSigns')}
                        />
                    }
                </div>
                {/* <CTags
                    clickable
                    tags={signsList}
                    gap
                    selectedTags={filtersState.selectedSigns}
                    onChange={(value) => handleUpdateStateArray('selectedSigns', value)}
                    onReset={() => handleUpdateStateArray('selectedSigns')}
                /> */}
                <div className="members-filters-row">
                    <div  className="members-filters-col">
                        {
                            hasOrganizationFilter &&
                            <FiltersOrganization
                                organizationsList={organizationsList}
                                organization={filtersState.suggestionOrganization}
                                onFilter={(value) => setFiltersState({suggestionOrganization: value})}
                            />
                        }
                        {
                            (hasTagsFilter && !!tagsList.length) &&
                            <FiltersTags
                            tags={tagsList}
                            selectedTags={filtersState.selectedTags}
                            onChange={(value) => handleUpdateStateArray('selectedTags', value)}
                            onReset={() => handleUpdateStateArray('selectedTags')}
                            />
                        }
                    </div>
                    {
                        hasSearch &&
                        <FiltersSearch
                            value={filtersState.searchQuery}
                            onSearch={(value) => setFiltersState({searchQuery: value})}
                        />
                    }
                </div>
            </div>
        </>
    );
}

export default MembersPageFilters;
